import {
  BrainCircuit,
  Database,
  Cloud,
  Code2,
  Smartphone,
  GitBranch,
  BarChart3,
  Compass,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "ai-solutions",
    title: "AI Solutions",
    icon: BrainCircuit,
    short:
      "Custom machine learning, LLM-powered apps, computer vision, and intelligent automation.",
    long:
      "We design and ship production-grade AI — from RAG and agentic systems to vision and forecasting models — engineered for reliability, observability and cost-efficiency.",
    highlights: [
      "LLM apps & RAG pipelines",
      "Predictive & forecasting models",
      "Computer Vision systems",
      "MLOps & model monitoring",
    ],
  },
  {
    slug: "data-engineering",
    title: "Data Engineering",
    icon: Database,
    short:
      "Modern data platforms, lakehouses, real-time pipelines, and rock-solid data quality.",
    long:
      "Composable data platforms built on Snowflake, Databricks, BigQuery and open-source. Streaming, batch and CDC pipelines with strong contracts and lineage.",
    highlights: [
      "Lakehouse architecture",
      "Streaming & CDC pipelines",
      "Data quality & lineage",
      "Cost-optimized warehousing",
    ],
  },
  {
    slug: "cloud-engineering",
    title: "Cloud Engineering",
    icon: Cloud,
    short:
      "Cloud-native architectures on AWS, GCP and Azure — secure, scalable, and FinOps-friendly.",
    long:
      "We architect and migrate workloads to the cloud with IaC, zero-trust security, multi-region resilience and FinOps disciplines from day one.",
    highlights: [
      "Cloud migration & modernization",
      "Kubernetes & serverless",
      "Zero-trust security",
      "FinOps & cost controls",
    ],
  },
  {
    slug: "web-development",
    title: "Web Development",
    icon: Code2,
    short:
      "High-performance web platforms, SaaS dashboards, and headless commerce experiences.",
    long:
      "React, Next.js, and Node.js-powered web platforms with stellar performance, accessibility and a meticulous focus on design and DX.",
    highlights: [
      "SaaS & portals",
      "Headless commerce",
      "Performance & Core Web Vitals",
      "Design systems",
    ],
  },
  {
    slug: "mobile-development",
    title: "Mobile App Development",
    icon: Smartphone,
    short:
      "Native iOS & Android and cross-platform React Native apps with a premium UX.",
    long:
      "Ship beautiful, performant mobile apps. We cover product strategy, UX, native modules, offline-first, push, and store delivery.",
    highlights: [
      "iOS, Android, React Native",
      "Offline-first architectures",
      "Push & realtime sync",
      "App store delivery",
    ],
  },
  {
    slug: "devops-automation",
    title: "DevOps & Automation",
    icon: GitBranch,
    short:
      "CI/CD, IaC, observability and platform engineering that accelerates delivery.",
    long:
      "Golden paths for shipping software fast and safely — pipelines, Terraform, Kubernetes, and observability stacks designed around your teams.",
    highlights: [
      "CI/CD & GitOps",
      "Terraform & Pulumi IaC",
      "Observability (OTel)",
      "Platform engineering",
    ],
  },
  {
    slug: "data-analytics-bi",
    title: "Data Analytics & BI",
    icon: BarChart3,
    short:
      "Self-serve analytics, semantic layers, and decision-grade dashboards.",
    long:
      "From metrics layers to executive dashboards — we make data actionable with dbt, Looker, Power BI, Tableau and Metabase.",
    highlights: [
      "Semantic / metrics layer",
      "Executive dashboards",
      "Customer & product analytics",
      "Embedded analytics",
    ],
  },
  {
    slug: "technology-consulting",
    title: "Technology Consulting",
    icon: Compass,
    short:
      "Strategy, architecture reviews, and digital transformation advisory.",
    long:
      "Independent, senior-led advisory on architecture, build-vs-buy, AI strategy, and modernization roadmaps — grounded in delivery experience.",
    highlights: [
      "AI & data strategy",
      "Architecture reviews",
      "Modernization roadmaps",
      "Fractional CTO engagements",
    ],
  },
];

export const STATS = [
  { value: "8+", label: "Service Domains" },
  { value: "24/7", label: "Engineering Support" },
  { value: "100%", label: "Outcome Focused" },
  { value: "∞", label: "Possibilities Unlocked" },
];

export const TESTIMONIALS = [
  {
    quote:
      "TriNexa rebuilt our analytics platform end-to-end. We now ship insights 5x faster, and our AI pipeline finally feels production-grade.",
    name: "Aarav Mehta",
    title: "VP Engineering, FintechCo",
  },
  {
    quote:
      "Their AI team brought clarity to a problem we had wrestled with for months. The RAG system they shipped is now central to our product.",
    name: "Priya Raghavan",
    title: "Chief Product Officer, HealthAI Labs",
  },
  {
    quote:
      "From cloud architecture to DevOps automation, TriNexa is the partner we wish we had hired two years ago.",
    name: "Daniel Okafor",
    title: "CTO, LogiStream",
  },
];

export const BLOG_POSTS = [
  {
    slug: "production-grade-rag-2026",
    title: "Designing Production-Grade RAG Systems in 2026",
    category: "AI",
    date: "Nov 12, 2025",
    excerpt:
      "RAG has moved past prototypes. Here's how we architect retrieval, evaluation and observability for enterprise-grade systems.",
    cover:
      "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    readTime: "8 min read",
  },
  {
    slug: "lakehouse-vs-warehouse",
    title: "Lakehouse vs. Warehouse: A Pragmatic 2026 Take",
    category: "Data",
    date: "Oct 28, 2025",
    excerpt:
      "Lakehouses are not a silver bullet. We share when to use each and how to combine them for cost and velocity.",
    cover:
      "https://images.unsplash.com/photo-1727434032773-af3cd98375ba?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    readTime: "6 min read",
  },
  {
    slug: "platform-engineering-golden-paths",
    title: "Platform Engineering: Golden Paths That Actually Work",
    category: "DevOps",
    date: "Oct 14, 2025",
    excerpt:
      "How we build internal developer platforms that engineers love — pragmatic patterns, anti-patterns, and KPIs.",
    cover:
      "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    readTime: "7 min read",
  },
  {
    slug: "finops-for-ai-workloads",
    title: "FinOps for AI Workloads — A CTO's Guide",
    category: "Cloud",
    date: "Sep 30, 2025",
    excerpt:
      "GPU spend can spiral fast. Here's a battle-tested playbook for governing AI infrastructure costs.",
    cover:
      "https://images.unsplash.com/photo-1775709610612-c589e29679ea?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    readTime: "9 min read",
  },
  {
    slug: "metrics-layer-truth",
    title: "The Metrics Layer Is the Single Source of Truth",
    category: "Analytics",
    date: "Sep 16, 2025",
    excerpt:
      "Why semantic layers are the missing piece for trustworthy BI — and how to roll one out without disrupting teams.",
    cover:
      "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    readTime: "5 min read",
  },
  {
    slug: "mobile-offline-first",
    title: "Offline-First Mobile Apps for Emerging Markets",
    category: "Mobile",
    date: "Aug 22, 2025",
    excerpt:
      "Patterns for resilient sync, conflict resolution and battery-friendly performance on flaky networks.",
    cover:
      "https://images.unsplash.com/photo-1727434032773-af3cd98375ba?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    readTime: "6 min read",
  },
];

export const BLOG_CATEGORIES = ["All", "AI", "Data", "DevOps", "Cloud", "Analytics", "Mobile"];

export const JOBS = [
  {
    slug: "senior-ai-engineer",
    title: "Senior AI Engineer",
    department: "Engineering",
    location: "Bangalore / Remote",
    type: "Full-time",
    summary:
      "Architect and ship LLM and ML systems for enterprise clients. Own end-to-end design from data to production.",
  },
  {
    slug: "data-engineer",
    title: "Data Engineer",
    department: "Data Platform",
    location: "Bangalore / Remote",
    type: "Full-time",
    summary:
      "Build modern lakehouse pipelines, real-time streaming and high-quality data products.",
  },
  {
    slug: "cloud-architect",
    title: "Cloud Solutions Architect",
    department: "Cloud",
    location: "Bangalore",
    type: "Full-time",
    summary:
      "Design secure, scalable cloud-native systems on AWS/GCP/Azure. IaC, Kubernetes, FinOps.",
  },
  {
    slug: "senior-fullstack",
    title: "Senior Full-Stack Engineer",
    department: "Product",
    location: "Bangalore / Remote",
    type: "Full-time",
    summary:
      "Ship beautiful, performant SaaS products using React, Node.js and a polished design system.",
  },
  {
    slug: "devops-engineer",
    title: "DevOps Engineer",
    department: "Platform",
    location: "Bangalore / Remote",
    type: "Full-time",
    summary:
      "Build CI/CD pipelines, observability stacks and platform tools that accelerate engineers.",
  },
  {
    slug: "bi-analytics-lead",
    title: "BI & Analytics Lead",
    department: "Data",
    location: "Bangalore",
    type: "Full-time",
    summary:
      "Lead analytics engagements with semantic layers, dbt and modern BI tooling.",
  },
];

export const VALUES = [
  {
    title: "Outcomes over output",
    description:
      "We measure success by the impact we create — not lines of code shipped.",
  },
  {
    title: "Engineering excellence",
    description:
      "Pragmatic craft, rigorous reviews, and a deep respect for production reality.",
  },
  {
    title: "Client partnership",
    description:
      "We embed deeply with your team and share the long-term outcomes we build.",
  },
  {
    title: "Responsible AI",
    description:
      "We build AI that is observable, evaluated, and aligned with your customers.",
  },
];
